/**
 * Error Queries Resource
 *
 * Thin 1:1 mapping to the errors query API. No business logic, no default
 * parameter injection. The SDK passes params as-is.
 *
 * Named error-queries.ts to avoid conflict with the SDK's errors.ts
 * (which defines APIError, NetworkError, TimeoutError).
 */

import type { HttpLayer } from '../http.js';
import type {
  ListErrorsRequest,
  ListErrorsResponse,
  ErrorCountsRequest,
  ErrorCountsResponse,
  DismissErrorsRequest,
  SetErrorsReadRequest,
  MutateErrorsResponse,
} from '../types/index.js';

export class ErrorQueriesResource {
  constructor(private http: HttpLayer) {}

  /**
   * List error events for the caller's tenant.
   *
   * Set `apply_mute: true` to drop rows whose `service_origin` the caller has
   * muted via `notifications.setPrefs`, counting `total` under the same
   * predicate. It defaults to false, so a caller that omits it sees every row
   * regardless of mute preferences. Mute is non-lossy — a muted row is still
   * stored and returned once the origin is unmuted.
   */
  async listErrors(
    params: ListErrorsRequest,
  ): Promise<ListErrorsResponse> {
    return this.http.post('/v1/errors/list', params);
  }

  async errorCounts(
    params: ErrorCountsRequest,
  ): Promise<ErrorCountsResponse> {
    return this.http.post('/v1/errors/counts', params);
  }

  /** Dismiss (clear) error events so they no longer surface in listErrors. */
  async dismiss(
    params: DismissErrorsRequest,
  ): Promise<MutateErrorsResponse> {
    return this.http.post('/v1/errors/dismiss', params);
  }

  /** Set read/unread state on error events. */
  async markRead(
    params: SetErrorsReadRequest,
  ): Promise<MutateErrorsResponse> {
    return this.http.post('/v1/errors/read', params);
  }
}
